import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { CommonModule, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';

import { JwtModule } from '@auth0/angular-jwt';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { ClipboardModule } from 'ngx-clipboard';
import { NgxMasonryModule } from 'ngx-masonry';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ToastrModule } from 'ngx-toastr';
import { AuthModule } from '@auth0/auth0-angular';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

// Components
import { BulkTagAddComponent } from '@components/bulk-tag-add/bulk-tag-add.component';
import { BulkDeleteDialogComponent } from '@components/bulk-delete-dialog/bulk-delete-dialog.component';
import { AdvanceQueryDialogComponent } from '@components/advance-query-dialog/advance-query-dialog.component';
import { BulkDownloadDialogComponent } from '@components/bulk-download-dialog/bulk-download-dialog.component';
import { AddCropDialogComponent } from '@components/add-crop-dialog/add-crop-dialog.component';
import { TagAddComponent } from '@components/tag-add/tag-add.component';
import { PendingDeleteWarningDialogComponent } from '@components/pending-delete-warning-dialog/pending-delete-warning-dialog.component';

// Pages
import { ImageViewPageComponent } from '@pages/image-view-page/image-view-page.component';
import { NotFoundPageComponent } from '@pages/not-found-page/not-found-page.component';
import { ImageDetailDummyPageComponent } from '@pages/image-detail-dummy-page/image-detail-dummy-page.component';

// Services
import { ImageService } from '@services/image.service';
import { TagService } from '@services/tag.service';
import { CropResolutionService } from '@services/cropresolution.service';
import { NotificationService } from '@services/notification.service';
import { AblyService } from '@services/ably.service';
import { WindowRef } from '@services/window.service';
import { CsvService } from '@services/csv.service';
import { ImageGraphqlService } from '@services/image-graphql.service';
import { TagGraphqlService } from '@services/tag-graphql.service';
import { CropresolutionGraphqlService } from '@services/cropresolution-graphql.service';
import { StatusGraphqlService } from '@services/status-graphql.service';

// Modules
import { SharedModule } from '@modules/shared.module';
import { GraphQLModule } from '@modules/graphql.module';

// Actions
import { ImageActions } from '@actions/image.actions';
import { SelectedImageActions } from '@actions/selectedimage.actions';
import { TagActions } from '@actions/tag.actions';
import { CropResolutionActions } from '@actions/cropresolution.actions';
import { AblyActions } from '@actions/ably.actions';

// Reducers
import { ImageReducer } from '@reducers/image';
import { ImageSearchReducer } from '@reducers/imagesearch';
import { SelectedImageReducer } from '@reducers/selectedimage';
import { TagReducer } from '@reducers/tag';
import { ManagerTagsReducer } from '@reducers/manager-tags';
import { TagSearchReducer } from '@reducers/tagsearch';
import { CropResolutionReducer } from '@reducers/cropresolution';
import { AblyReducer } from '@reducers/ably';

// Effects
import { ImageEffects } from '@effects/image.effects';
import { TagEffects } from '@effects/tag.effects';
import { CropResolutionEffects } from '@effects/cropresolution.effects';

import { MicroSentryModule } from '@micro-sentry/angular';

registerLocaleData(localeEn);
export function tokenGetter() {
    return localStorage.getItem('id_token');
}

@NgModule({
    declarations: [
        AppComponent,
        ImageDetailDummyPageComponent,
        ImageViewPageComponent,
        NotFoundPageComponent,
        TagAddComponent,
        BulkTagAddComponent,
        BulkDownloadDialogComponent,
        BulkDeleteDialogComponent,
        AddCropDialogComponent,
        AdvanceQueryDialogComponent,
        PendingDeleteWarningDialogComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        AppRoutingModule,
        ToastrModule.forRoot({
            positionClass: 'toast-bottom-right',
            timeOut: 5000
        }),
        JwtModule.forRoot({
            config: {
                tokenGetter: tokenGetter,
                allowedDomains: [
                    'api.eu.apiconnect.ibmcloud.com',
                    'service.us.apiconnect.ibmcloud.com',
                    'service.eu-de.apiconnect.ibmcloud.com',
                    'b48cgnc5ra.execute-api.us-east-1.amazonaws.com',
                    'eddosgymxl.execute-api.eu-central-1.amazonaws.com'
                ]
            }
        }),
        AuthModule.forRoot({
            domain: 'login.mediaspirit.io',
            clientId: 'tqNF2in8RSQkYIFuvjTxGXLZTA1dMEzT',
            authorizationParams: {
                redirect_uri: window.location.origin, // The callback URL after login
                scope: 'openid profile email' // Scopes for tokens
            },
            cacheLocation: 'localstorage', // Use local storage to persist the token
            useRefreshTokens: true        // Enable refresh tokens for session renewal
        }),
        StoreModule.forRoot({
            image: ImageReducer,
            imagesearch: ImageSearchReducer,
            tag: TagReducer,
            tagsearch: TagSearchReducer,
            cropresolution: CropResolutionReducer,
            selectedimage: SelectedImageReducer,
            managerTags: ManagerTagsReducer,
            ablyState: AblyReducer
        }),
        EffectsModule.forRoot([ImageEffects, TagEffects, CropResolutionEffects]),
        ClipboardModule,
        NgxMasonryModule,
        NgxPageScrollModule,
        NgxPageScrollCoreModule,
        InfiniteScrollModule,
        SharedModule,
        GraphQLModule,
        MicroSentryModule.forRoot({
            dsn: 'https://95763dc442f249cfa35880e7e6a16277@glitchtip.travelspirit.cloud/4',
        })
    ],
    providers: [
        SelectedImageActions,
        AblyActions,
        ImageService,
        ImageActions,
        TagService,
        TagActions,
        CropResolutionActions,
        CropResolutionService,
        NotificationService,
        AblyService,
        WindowRef,
        { provide: LOCALE_ID, useValue: 'en-gb' },
        CsvService,
        ImageGraphqlService,
        TagGraphqlService,
        CropresolutionGraphqlService,
        StatusGraphqlService,
        provideHttpClient(withInterceptorsFromDi())
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }

